import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import MainProgressBar from '../components/MainProgressBar';
import { fetchTotalBurned } from '../services/api';
import TotalProgressBar from '../components/TotalProgressBar';
import { formatNumber } from '../utils';
import toast, { Toaster } from 'react-hot-toast';

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: white;
  padding-top: 100px;
`;

const CopyIcon = styled.img`
  width: 20px;
  height: 20px;
  margin-left: 5px;
`;

const SubTitle = styled.h2`
  font-size: 1.25rem;
  color: #a1a1aa;
  margin-bottom: 30px;
  text-align: center;
`;

const ButtonsContainer = styled.div`
  position: absolute;
  top: 20px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  padding: 0 20px;
`;

const Button = styled.a`
  background: #b092d6;
  color: white;
  padding: 12px 20px;
  border-radius: 30px;
  text-decoration: none;
  font-weight: bold;
  text-transform: uppercase;
  font-size: 1rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: background-color 0.3s ease, transform 0.2s ease, box-shadow 0.3s ease;

  &:hover {
    background-color: #ff8c00;
    transform: translateY(-2px);
    box-shadow: 0 8px 12px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(1px);
    box-shadow: 0 6px 8px rgba(0, 0, 0, 0.1);
  }

  &:focus {
    outline: none;
    border: 2px solid #b092d6;
  }
`;

const Img = styled.img`
  width: 180px;
  height: 180px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    width: 120px;
    height: 120px;
  }
`;

const InfoSection = styled.div`
  margin-top: 40px;
  text-align: center;
  width: 100%;
`;

const AddressContainer = styled.div`
  padding: 15px;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
  font-size: 1.1rem;
  font-weight: bold;
  color: #fff;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const WalletAddress = styled.span`
  font-size: 1.1rem;
  margin-bottom: 10px;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BurningSupplyContainer = styled.div`
  font-size: 1.1rem;
  font-weight: bold;
  color: #fff;
  margin-top: 20px;
  text-align: center;
`;

const Label = styled.div`
  font-size: 1rem;
  color: #a1a1aa;
  margin-bottom: 10px;
`;

const goals = [
  2_000_000, 3_000_000, 4_000_000, 3_000_000, 6_000_000, 7_000_000, 5_000_000, 4_000_000, 7_000_000,
  9_000_000,
];

const Home: React.FC = () => {
  const [burnedAmount, setBurnedAmount] = useState(0);
  const [currentGoalIndex, setCurrentGoalIndex] = useState(0);
  const totalGoal = goals.reduce((total, goal) => total + goal, 0);
  const maxLevel = goals.length;

  // Calculate the cumulative goals to determine the current level
  const calculateCurrentLevel = (burned: number) => {
    let cumulative = 0;
    for (let i = 0; i < goals.length; i++) {
      cumulative += goals[i];
      if (burned < cumulative) {
        return i;
      }
    }
    return goals.length - 1; // return last index if burned amount exceeds all goals
  };

  // Calculate the current goal progress
  const calculateCurrentGoalProgress = (burned: number, levelIndex: number) => {
    // Calculate the sum of all previous goals
    const previousGoalsSum = goals.slice(0, levelIndex).reduce((sum, goal) => sum + goal, 0);
    const currentGoal = goals[levelIndex];
    const burnedInCurrentGoal = burned - previousGoalsSum;

    return {
      currentGoal,
      burnedInCurrentGoal,
      percentage: Math.min((burnedInCurrentGoal / currentGoal) * 100, 100),
    };
  };

  const walletAddress = 'J1m9ZyzpmoeaY8aTxtXmmdrsbABxvMeLcjf3BkYKtP3e';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const amount = await fetchTotalBurned();
        setBurnedAmount(amount);

        // Calculate the current level based on total burned amount
        const newLevelIndex = calculateCurrentLevel(amount);
        setCurrentGoalIndex(newLevelIndex);

        // Show level up notification if needed
        if (newLevelIndex > currentGoalIndex) {
          toast.success(
            `Level ${newLevelIndex + 1} reached! New goal: ${formatNumber(goals[newLevelIndex])}`
          );
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to fetch burned amount. Please try again later.');
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [currentGoalIndex]);

  const { currentGoal, burnedInCurrentGoal, percentage } = calculateCurrentGoalProgress(
    burnedAmount,
    currentGoalIndex
  );
  const level = currentGoalIndex + 1;

  const handleCopy = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(walletAddress).then(() => {
        toast.success('Wallet address copied to clipboard!');
      });
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = walletAddress;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Wallet address copied to clipboard!');
    }
  };

  return (
    <MainContainer>
      <Toaster />
      <ButtonsContainer>
        <Button href="https://linktr.ee/solamasol" target="_blank">
          Socials
        </Button>
        <Button href="docs/doc_solama.pdf" target="_blank">
          More Info
        </Button>
      </ButtonsContainer>
      <Img src={'/images/logo.png'} alt="Solama Factory" />
      <SubTitle>Join the excitement of burning tokens and reaching milestones!</SubTitle>
      <MainProgressBar
        percentage={percentage}
        label={`Current Goal: ${formatNumber(currentGoal)}`}
        total={formatNumber(currentGoal)}
        level={level}
        burned={formatNumber(burnedInCurrentGoal)}
        maxLevel={maxLevel}
      />
      <TotalProgressBar
        percentage={totalGoal > 0 ? (burnedAmount / totalGoal) * 100 : 0}
        total={formatNumber(totalGoal)}
        burned={formatNumber(burnedAmount)}
      />

      {/* New Section: Burning Wallet and Burning Supply */}
      <InfoSection>
        <AddressContainer onClick={handleCopy}>
          <Label>BURNING WALLET</Label>
          <WalletAddress>{walletAddress}</WalletAddress>
          <CopyIcon src={'/images/copy.png'} alt="Copy" />
        </AddressContainer>

        <BurningSupplyContainer>
          <Label>BURNED SUPPLY</Label>
          <div>
            {formatNumber(burnedAmount)} | {formatNumber(676000000)}
          </div>
        </BurningSupplyContainer>
      </InfoSection>
    </MainContainer>
  );
};

export default Home;
